import React from 'react';
import { Clock, Users, Database, TrendingDown, ArrowRight, Zap, LucideIcon } from 'lucide-react';
import '../styles/Problems.css';

interface PainPoint {
  id: number;
  icon: LucideIcon;
  title: string;
  description: string;
  outcome: string;
}

const PAIN_POINTS: PainPoint[] = [
  {
    id: 1,
    icon: Clock,
    title: 'Manual work eats the calendar',
    description: 'Every week, hours disappear into ticket triage, data entry, invoice chasing, and copy-paste between systems.',
    outcome: 'Agents that handle routine work 24/7.',
  },
  {
    id: 2,
    icon: Users,
    title: 'You grew—but the backoffice didn\'t scale',
    description: 'Onboarding a new client, hire, or vendor still needs a human to copy fields into six SaaS tools.',
    outcome: 'Automations that scale with headcount.',
  },
  {
    id: 3,
    icon: Database,
    title: 'Data lives in fifteen systems',
    description: 'Sales, support, finance, and ops each have their own source of truth. Reporting means asking the same question three times.',
    outcome: 'Unified pipelines and dashboards.',
  },
  {
    id: 4,
    icon: TrendingDown,
    title: 'Off-the-shelf AI doesn\'t fit',
    description: 'Generic chatbots miss your process, your voice, and your edge cases. You need AI that understands your operation.',
    outcome: 'Custom agents trained on your context.',
  },
];

const Problems: React.FC = () => {
  return (
    <section id="problems" className="problems section-padding">
      <div className="container">
        <div className="section-kicker reveal">
          <span className="eyebrow">The Status Quo</span>
          <h2>Your team is spending the wrong hours on the wrong things.</h2>
          <p>
            We&rsquo;ve seen the same four stalls across every company we&rsquo;ve worked with.
            Our job is to remove them—quietly, reliably, permanently.
          </p>
        </div>

        <div className="problems-grid">
          {PAIN_POINTS.map((p) => {
            const Icon = p.icon;
            return (
              <article key={p.id} className="pain-card reveal">
                <div className="pain-icon">
                  <Icon size={18} />
                </div>
                <h3>{p.title}</h3>
                <p>{p.description}</p>
                <div className="pain-outcome">
                  <Zap size={14} />
                  <span>{p.outcome}</span>
                  <ArrowRight size={14} className="outcome-arrow" />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Problems;
